import type OrganizationWorkflowView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * The current workflows of one organization, scoped by `organizationId`, in the server's order: the
 * automatic transcriptions first, then the document groups by label. Never re-sorted here.
 */
class OrganizationWorkflowsView {
	readonly list: readonly OrganizationWorkflowView[];
	readonly organizationId: number;

	constructor(list: readonly OrganizationWorkflowView[], organizationId: number) {
		this.list = list;
		this.organizationId = organizationId;

		Object.freeze(this);
	}

	/** The automatic transcriptions (one for the interviews, one for the notes). */
	get automaticWorkflows(): readonly OrganizationWorkflowView[] {
		return this.list.filter((workflow) => workflow.isAutomatic);
	}

	/** The composable groups, active or archived. */
	get documentGroups(): readonly OrganizationWorkflowView[] {
		return this.list.filter((workflow) => !workflow.isAutomatic);
	}

	findByKey(key: string): OrganizationWorkflowView | null {
		return this.list.find((workflow) => workflow.key === key) ?? null;
	}

	/** Replaces the workflow sharing the entry's key in place, or appends it. */
	upsertWorkflow(workflow: OrganizationWorkflowView): OrganizationWorkflowsView {
		const isKnown = this.list.some((entry) => entry.key === workflow.key);

		return new OrganizationWorkflowsView(
			isKnown
				? this.list.map((entry) => (entry.key === workflow.key ? workflow : entry))
				: [...this.list, workflow],
			this.organizationId,
		);
	}

	setWorkflowActive(key: string, isActive: boolean): OrganizationWorkflowsView {
		return new OrganizationWorkflowsView(
			this.list.map((workflow) => (workflow.key === key ? workflow.withActive(isActive) : workflow)),
			this.organizationId,
		);
	}
}

export default OrganizationWorkflowsView;
